import { useCallback, useEffect, useRef, useState } from "react";

const useFundraiserSession = () => {
  const [fundraiserSessionToken, setFundraiserSessionToken] = useState("");
  const [fundraiserSessionExpiresAt, setFundraiserSessionExpiresAt] = useState(null);
  const fundraiserSessionTokenRef = useRef("");
  const expiryTimeoutRef = useRef(null);
  const expiryHandlerRef = useRef(null);

  const clearExpiryTimeout = useCallback(() => {
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  }, []);

  const clearFundraiserSession = useCallback(() => {
    clearExpiryTimeout();
    fundraiserSessionTokenRef.current = "";
    setFundraiserSessionToken("");
    setFundraiserSessionExpiresAt(null);
  }, [clearExpiryTimeout]);

  const setFundraiserSession = useCallback(
    (sessionToken, sessionExpiresAt) => {
      clearExpiryTimeout();

      const token = sessionToken || "";
      const parsedExpiresAt =
        sessionExpiresAt === null ||
        sessionExpiresAt === undefined ||
        sessionExpiresAt === ""
          ? null
          : Number(sessionExpiresAt);
      const expiresAt =
        parsedExpiresAt !== null && Number.isFinite(parsedExpiresAt)
          ? parsedExpiresAt
          : null;

      fundraiserSessionTokenRef.current = token;
      setFundraiserSessionToken(token);
      setFundraiserSessionExpiresAt(expiresAt);

      if (!token || expiresAt === null) return;

      const expiresIn = expiresAt - Date.now();
      if (expiresIn <= 0) {
        clearFundraiserSession();
        if (expiryHandlerRef.current) expiryHandlerRef.current();
        return;
      }

      expiryTimeoutRef.current = setTimeout(() => {
        clearFundraiserSession();
        if (expiryHandlerRef.current) expiryHandlerRef.current();
      }, expiresIn);
    },
    [clearExpiryTimeout, clearFundraiserSession]
  );

  const setExpiryHandler = useCallback((handler) => {
    expiryHandlerRef.current = handler;
  }, []);

  useEffect(() => {
    return () => clearExpiryTimeout();
  }, [clearExpiryTimeout]);

  return {
    fundraiserSessionToken,
    fundraiserSessionTokenRef,
    fundraiserSessionExpiresAt,
    setFundraiserSession,
    clearFundraiserSession,
    setExpiryHandler,
  };
};

export default useFundraiserSession;